import { group, topic } from "./schema.js";

export const BROKER_META = {
  role: "broker",
  title: "Broker Manual",
  kicker: "Supervise the office without turning it into a race",
  whyHeading: "Why This Helps Your Office",
};

export const BROKER_TREE = [
  topic({
    slug: "start-here",
    title: "Start Here",
    terms: ["overview", "supervision", "getting started"],
    forYou:
      "This book is for brokers using Broker Desk. You will know most of it. The rest is here so a control that does not click never leaves you stuck on a live file. The Guided Tour is the film. This is the book. Clients and agents have their own.",
    how: [
      "Sign in as broker so the desk matches the office.",
      "Use My Business when you mean your book. Use All Agents when you mean the office.",
      "Open files by type — buyer and seller carpets never merge.",
      "Coach in Office Messaging. Keep Client Messaging for the file.",
      "Close as a habit. Retention is Tuesday, not a scramble when someone asks for history.",
    ],
    why:
      "An office that can find every file, every notice, and every custom step without folklore is an office that stays kind under pressure. Clients feel that even when they never see this desk. People who enjoy their work notice when someone provided an easy system for a new tool.",
    examples:
      "Week-one huddle: Guided Tour, then “User Guide is on your sub-nav — it opens this site. Search before you invent a workaround.”",
    tips: [
      "Four books, one product. Never send a seller the Buyer Manual as if it were theirs.",
      "If you ever feel unsure, the sidebar search can guide you instantly.",
    ],
    related: ["broker-desk", "manage-agents", "office-steps"],
    href: "/broker/dashboard",
  }),

  group("The desk", [
    topic({
      slug: "broker-desk",
      title: "Broker Desk",
      terms: ["dashboard", "supervision", "my business"],
      forYou:
        "Broker Desk is a framed desk for the office, not a scoreboard. Culture is the tone of this screen.",
      how: [
        "Start on Dashboard.",
        "Then the file that actually needs you.",
        "Fill Broker Profile in week one so the footer looks like an office.",
      ],
      why:
        "Empty cards look like empty leadership. A calm desk teaches the office how to sound — in the archive, not only in the huddle.",
      examples:
        "If the calendar is full of community expos and empty of training, add a desk event for huddle.",
      tips: ["Billing may link to Pay on this site. That is not this manual, and not a client thread."],
      related: ["manage-agents", "client-manager", "office-steps"],
      href: "/broker/dashboard",
    }),
    topic({
      slug: "calendar",
      title: "Calendar",
      terms: ["office dates", "huddle", "hub"],
      forYou:
        "Office dates you write as desk events; community dates from the hub when they are relevant and read-only.",
      how: [
        "Create training and opens as desk events when they are the office’s own.",
        "Let the hub list a county forum. Do not copy hub rows into Client Messaging as if you host them.",
      ],
      why:
        "The office should not live on a whiteboard only. Two clocks, one screen. Leadership is knowing which is which.",
      examples:
        "First-Tuesday huddle is a desk event. A national convention may be hub.",
      tips: ["San Diego County first on hub defaults. Southern California next."],
      related: ["office-steps", "manage-agents"],
      href: "/broker/calendar",
    }),
    topic({
      slug: "manage-agents",
      title: "Managing agents",
      terms: ["coaching", "all agents", "seats", "huddle"],
      forYou:
        "Managing agents is coaching on the desk they actually have — assigned work, two messaging rooms, packets, calendar.",
      how: [
        "Flip All Agents when you mean to see the office.",
        "Open the file. Flip back when you are done.",
        "Coach in Office Messaging with one specific note — not a lecture in the client thread.",
        "Send them the Agent Manual when a control does not click.",
      ],
      why:
        "People who enjoy their work notice when the office gave them a book. Accidental office-wide view is sloppy even when it is allowed. Privacy is a posture.",
      examples:
        "New agent lost in Custom Steps: send the Agent Manual, then sit with one file together.",
      tips: ["The switch is a responsibility, not a toy. Labeled. Reversible."],
      related: ["office-steps", "client-manager", "notices"],
    }),
    topic({
      slug: "client-manager",
      title: "Client management",
      terms: ["office clients", "assigned", "people"],
      forYou:
        "Client Manager is people at office scope or your book — depending on the switch — not a lead feed.",
      how: [
        "Set All Agents or My Business first.",
        "Then open a person, then Files.",
        "If photos are empty on a seller, coach in Office, then let their agent send a kind Client Message — unless policy says you step in.",
      ],
      why:
        "You cannot supervise a name you cannot find. Scope first, then the person. That order keeps privacy labeled.",
      examples:
        "A seller with empty photos is a coaching moment, not a public shaming in the client thread.",
      tips: ["Do not keep a shadow spreadsheet."],
      related: ["manage-agents", "documents-photos"],
      href: "/broker/clients",
    }),
  ]),

  group("Office work", [
    topic({
      slug: "office-steps",
      title: "Office-level steps",
      terms: ["custom steps", "lockbox", "signs", "vendors"],
      forYou:
        "Office-level steps are the rituals you want repeated — lockbox, signs, photographer — modeled on the packet, not in a binder only.",
      how: [
        "Open a file. Add a step. Status it.",
        "Put the vendor you trust in the note. This is not a bid wall or a marketplace.",
        "Expect agents to copy the pattern.",
      ],
      why:
        "Rituals on the desk get done. Rituals in a binder get skipped. Clients never need to see the lockbox vendor. They feel the showing that happened on time.",
      examples:
        "“Call Coastal Stills for Tuesday” is a packet step. A public vendor grid on this site is a placeholder directory, not a store inside the file.",
      tips: ["Do not promise DRE-adjacent vendor certification. You are naming people you work with."],
      related: ["manage-agents", "documents-photos", "file-packet"],
    }),
    topic({
      slug: "notices",
      title: "Notices",
      terms: ["system notices", "broadcast", "security"],
      forYou:
        "Notices are the product’s voice — security, upgrades, releases — never mixed with client conversation.",
      how: [
        "Read and acknowledge on Notices.",
        "Mention in Office huddle if the office must act.",
        "Do not paste a notice into Client Messaging. Point people to Notices instead.",
      ],
      why:
        "A security note in a buyer thread looks like concealment even when you are being careful. Wrong room is the only real mistake.",
      examples:
        "When a notice posts: “Acknowledge Notices, don’t paste it to clients.” One huddle sentence. Culture kept.",
      tips: ["You do not author product notices here. You route people to them. Privacy and Terms stay in the footer."],
      related: ["manage-agents", "reports"],
      href: "/notices",
    }),
    topic({
      slug: "reports",
      title: "Reports",
      terms: ["export", "pdf", "retention", "archive"],
      forYou:
        "Reports are communication snapshots for the office archive. Retention is the habit attached to close.",
      how: [
        "Export from close or abandon when the file actually ends.",
        "Log retention the same day.",
        "Keep tone archive-worthy. A dissolved deal still deserves a calm export.",
      ],
      why:
        "Memory is not a retention policy. Offices that wait until someone asks for history already lost it. This is not a DRE certification workflow. It is an office memory.",
      examples:
        "Close on Tuesday. Log on Tuesday. That is the whole sermon.",
      tips: ["Sample reports on Admin are teaching copies, not client records. You and the agent are flagged in this prototype; client if opted in."],
      related: ["notices", "file-packet"],
    }),
    topic({
      slug: "documents-photos",
      title: "Documents & photos",
      terms: ["office vault", "media"],
      forYou:
        "Documents & Photos lets you see media at office scope without a public gallery.",
      how: [
        "Open the vault. Filter.",
        "If a seller set never left someone’s camera roll, coach the Photos page — in Office, then their agent in Client.",
      ],
      why:
        "You cannot judge a listing brief you cannot see. You also cannot dump JPEGs into Notices and call it a report.",
      examples:
        "Web-sized seller photos. Small PDFs for letters. Size limits still apply.",
      tips: ["Office policy governs what you store. Model it."],
      related: ["office-steps", "client-manager"],
      href: "/broker/documents",
    }),
    topic({
      slug: "file-packet",
      title: "File Packet",
      terms: ["broker files", "supervision"],
      forYou:
        "Every file in the brokerage can sit in a packet you can open — profile through PDF, custom steps, lifecycle.",
      how: [
        "Files under All Agents when you are supervising.",
        "My Business for your own clients if you still book.",
        "Confirm the file type badge. Inquiry/Response is the agent’s voice, not a merged form.",
      ],
      why:
        "You cannot coach a packet you cannot see. Cross-mode contamination is the product’s non-negotiable. If an agent asks to copy buyer pages for sellers, the answer is no.",
      examples:
        "Before an audit conversation, open Retention, then the packet, then the export. That order is a habit.",
      tips: ["Walk new agents through both client manuals once. It pays for itself in week two."],
      related: ["office-steps", "reports", "manage-agents"],
      href: "/broker/files",
    }),
    topic({
      slug: "messaging",
      title: "Messaging",
      terms: ["client", "office", "two rooms"],
      forYou:
        "Two rooms, one store: Client Messaging for files, Office Messaging for culture and inquiries.",
      how: [
        "Supervise Client threads without cruelty. If you must step in, say you are the broker and why, then let the agent keep the relationship.",
        "Coach in Office. Keep huddle jokes out of the file.",
        "Archive each room on its own path.",
      ],
      why:
        "Culture is this transcript. A second mailbox is how things get lost. We did not build one.",
      examples:
        "An apology to a buyer belongs in Client — preferably from their agent. A kind correction about lockbox timing is Office.",
      tips: ["Write as if the export will be read later. It will."],
      related: ["manage-agents", "reports"],
      href: "/broker/messages",
    }),
  ]),
];
